import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { getGuideColors } from 'app/features/leads/components/guide/guide-colors';

import IconAlert from 'app/assets/leads-guide/icon-alert.svg';
import Typography from 'app/ui/components/atoms/typography';

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  width: 100%;

  ${({ theme: { media, spacing } }) => media.medium(() => `gap: ${spacing(8)};`)}
`;

const Subtitle = styled(Typography)`
  text-align: center;
`;

const ItemList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
  margin-top: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media } }) => media.medium(() => 'margin-top: 0;')}
`;

const Item = styled.li`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  align-items: flex-start;
`;

const ItemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 0.0625rem solid ${({ theme }) => theme.semantic.accent_500};
  background-color: ${({ theme }) => theme.semantic.accent_100};
  color: ${({ theme }) => theme.semantic.accent_500};
  font-size: ${({ theme }) => theme.typography.tiny};
  font-weight: ${({ theme }) => theme.typography.bold};
  flex-shrink: 0;
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  min-width: 0;
`;

const ItemTitle = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.bold};
`;

const ReminderCard = styled.div`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  padding: ${({ theme: { spacing } }) => spacing(3)};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  border: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  background-color: ${({ theme }) => theme.semantic.neutrals_200};
  width: 100%;
  margin-top: ${({ theme: { spacing } }) => spacing(4)};

  img {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  ${({ theme: { media } }) => media.medium(() => 'margin-top: 0;')}
`;

const ReminderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const ReminderTitle = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.bold};
  font-size: ${({ theme }) => theme.typography.xsmall};
  line-height: 1.4;
`;

const ReminderBody = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.xsmall};
  line-height: 1.4;
`;

const LEAD_ITEMS = [
  {
    badge: '1',
    titleId: 'leadsGuide.step3.items.review.title',
    descriptionId: 'leadsGuide.step3.items.review.description',
  },
  {
    badge: '2',
    titleId: 'leadsGuide.step3.items.respond.title',
    descriptionId: 'leadsGuide.step3.items.respond.description',
  },
  {
    badge: '3',
    titleId: 'leadsGuide.step3.items.markOutcome.title',
    descriptionId: 'leadsGuide.step3.items.markOutcome.description',
  },
] as const;

export const LeadsStep: React.FC = () => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const guideColors = getGuideColors(semantic);

  const subtitle = intl.formatMessage({ id: 'leadsGuide.step3.subtitle' });
  const reminderTitle = intl.formatMessage({ id: 'leadsGuide.step3.reminder.title' });
  const reminderBody = intl.formatMessage({ id: 'leadsGuide.step3.reminder.body' });

  return (
    <StepContent data-testid="leads-guide-step-leads">
      <Subtitle variant="h5">{subtitle}</Subtitle>
      <ItemList>
        {LEAD_ITEMS.map((item) => (
          <Item key={item.titleId}>
            <ItemBadge>{item.badge}</ItemBadge>
            <ItemText>
              <ItemTitle variant="body2" color={guideColors.textPrimary}>
                {intl.formatMessage({ id: item.titleId })}
              </ItemTitle>
              <Typography variant="body2" color={guideColors.textSecondary}>
                {intl.formatMessage({ id: item.descriptionId })}
              </Typography>
            </ItemText>
          </Item>
        ))}
      </ItemList>
      <ReminderCard data-testid="leads-guide-reminder">
        <img src={IconAlert} alt="" />
        <ReminderText>
          <ReminderTitle variant="caption" color={guideColors.textAccent}>
            {reminderTitle}
          </ReminderTitle>
          <ReminderBody variant="caption" color={guideColors.textPrimary}>
            {reminderBody}
          </ReminderBody>
        </ReminderText>
      </ReminderCard>
    </StepContent>
  );
};
